import { computed, ref } from 'vue';

/**
 * Composable para gestionar información de alumnos con filtrado por grupo
 *
 * Proporciona:
 * - Estado reactivo de alumnos y filtros
 * - Datos filtrados por grupo (y extensible a otros filtros)
 * - Helpers para extraer grupos únicos
 *
 * Uso:
 * const { infoAlumnos, grupoSeleccionado, infoAlumnosFiltrados, gruposUnicos } =
 *   useInfoAlumnos({
 *     datosIniciales: [...alumnos...]
 *   });
 *
 * Extension points:
 * - TODO: Añadir soporte para filtros adicionales (asignatura, estado, etc.)
 * - TODO: Añadir ordenamientos personalizados más allá del grupo
 */
export default function useInfoAlumnos(opciones = {}) {
  const { datosIniciales = [] } = opciones;

  // ========== ESTADO REACTIVO ==========

  /**
   * Array completo de información de alumnos
   * Estructura de cada item: { alumno, grupo, numero, indicadores, ... }
   */
  const infoAlumnos = ref(datosIniciales);

  /**
   * Grupo actualmente seleccionado para filtrar
   * null = mostrar todos los alumnos
   * object = { id, etiqueta, ... } - mostrar solo alumnos de este grupo
   */
  const grupoSeleccionado = ref(null);

  // ========== COMPUTED PROPERTIES ==========

  /**
   * Información de alumnos filtrada según grupoSeleccionado
   * Si grupoSeleccionado es null, devuelve todos los alumnos
   */
  const infoAlumnosFiltrados = computed(() => {
    if (!grupoSeleccionado.value) {
      return infoAlumnos.value;
    }

    return infoAlumnos.value.filter(
      (info) => info.grupo.id === grupoSeleccionado.value.id,
    );
  });

  /**
   * Array de grupos únicos presentes en infoAlumnos, ordenados alfabéticamente
   * Estructura: [{ id, etiqueta }, ...]
   */
  const gruposUnicos = computed(() => {
    const gruposMap = {};

    infoAlumnos.value.forEach((info) => {
      if (info.grupo && !gruposMap[info.grupo.id]) {
        gruposMap[info.grupo.id] = info.grupo;
      }
    });

    return Object.values(gruposMap).sort((a, b) => {
      if (!a.etiqueta || !b.etiqueta) return 0;
      return a.etiqueta.localeCompare(b.etiqueta);
    });
  });

  // ========== MÉTODOS PÚBLICOS ==========

  /**
   * Actualiza el conjunto completo de datos de alumnos
   * Limpia el filtro de grupo actual
   */
  const setInfoAlumnos = (nuevosDatos) => {
    infoAlumnos.value = nuevosDatos;
    grupoSeleccionado.value = null;
  };

  /**
   * Establece el grupo seleccionado para filtrar
   * @param {Object|null} grupo - Grupo a filtrar o null para mostrar todos
   */
  const setGrupoSeleccionado = (grupo) => {
    grupoSeleccionado.value = grupo;
  };

  /**
   * Obtiene un alumno específico por su ID
   * @param {number} idAlumno - ID del alumno
   * @returns {Object} Objeto alumno o undefined si no existe
   */
  const getAlumnoById = (idAlumno) => {
    const info = infoAlumnos.value.find((item) => item.alumno.id === idAlumno);
    return info?.alumno;
  };

  /**
   * Obtiene la información completa de un alumno incluyendo grupo e indicadores
   * @param {number} idAlumno - ID del alumno
   * @returns {Object} Objeto info completo o undefined si no existe
   */
  const getInfoAlumnoById = (idAlumno) => (
    infoAlumnos.value.find((item) => item.alumno.id === idAlumno)
  );

  /**
   * Limpia los datos de alumnos
   */
  const limpiar = () => {
    infoAlumnos.value = [];
    grupoSeleccionado.value = null;
  };

  // ========== RETORNO PÚBLICO ==========

  return {
    // Estado reactivo
    infoAlumnos,
    grupoSeleccionado,

    // Computed properties
    infoAlumnosFiltrados,
    gruposUnicos,

    // Métodos
    setInfoAlumnos,
    setGrupoSeleccionado,
    getAlumnoById,
    getInfoAlumnoById,
    limpiar,
  };
}
